import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from "../firebase/firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";

// ─── constants ────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 'account',    label: 'Account',    icon: '🔐' },
  { id: 'personal',   label: 'About You',  icon: '👤' },
  { id: 'values',     label: 'Values',     icon: '💡' },
  { id: 'culture',    label: 'Background', icon: '🌍' },
  { id: 'lifestyle',  label: 'Lifestyle',  icon: '✨' },
  { id: 'family',     label: 'Family',     icon: '🏡' },
  { id: 'marriage',   label: 'Marriage',   icon: '💍' },
  { id: 'finish',     label: 'Finish',     icon: '🎉' },
];

const OPTIONS = {
  gender:            ['Male', 'Female', 'Non-Binary', 'Prefer Not to Say'],
  educationLevel:    ['High School', "Associate's Degree", "Bachelor's Degree", "Master's Degree", 'Doctorate', 'Other'],
  qualities:         ['Honesty', 'Kindness', 'Ambition', 'Intelligence', 'Humor', 'Reliability', 'Compassion'],
  traits:            ['Patience', 'Humor', 'Resilience', 'Empathy', 'Creativity', 'Optimism'],
  hobbies:           ['Reading', 'Traveling', 'Cooking', 'Sports', 'Music', 'Hiking', 'Arts & Crafts'],
  cultural:          ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jewish', 'Secular'],
  traditions:        ['Family Meals', 'Festivals', 'Respect for Elders', 'Family Gatherings', 'Religious Practices'],
  longTermGoals:     ['Career Advancement', 'Financial Stability', 'Travel', 'Starting a Family', 'Community Involvement'],
  lifestyle:         ['Urban Living', 'Suburban Living', 'Rural Living', 'Budget-Conscious', 'Financially Free-Spirited'],
  careerGoals:       ['Career-Oriented', 'Balanced Career & Family', 'Flexible / Family Focus', 'Stay-at-Home'],
  familyExpect:      ['Frequent Family Visits', 'Involvement in Decisions', 'Separate Family Roles', 'Support in Family Matters'],
  partnerFamily:     ['Respectful Interaction', 'Active Participation', 'Supportive Role'],
  health:            ['Healthy Eating', 'Regular Exercise', 'Smoking', 'Alcohol Consumption'],
  marriageRoles:     ['Equal Household Duties', 'Traditional Roles', 'Financial Contributions', 'Parenting Responsibilities'],
  futureVision:      ['Specific Location', 'Career Milestones', 'Starting a Family', 'Traveling Together'],
  conflictHandling:  ['Open Communication', 'Seeking Mediation', 'Avoiding Conflicts', 'Professional Help'],
  conflictStrategies:['Listening & Empathy', 'Compromise', 'Problem-Solving', 'Negotiation'],
  children:          ['Want Children', 'Undecided', 'Do Not Want Children'],
  finances:          ['Shared Finances', 'Separate Finances', 'Hybrid'],
  householdDuties:   ['Shared Equally', 'Traditional Roles', 'Flexible'],
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const toggle = (arr, val) =>
  arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

const validateEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// ─── sub-components ───────────────────────────────────────────────────────────
function ChipGroup({ options, selected, onChange, max }) {
  return (
    <div className="su-chips">
      {options.map(o => {
        const active = selected.includes(o);
        const disabled = !active && max && selected.length >= max;
        return (
          <button
            key={o} type="button"
            className={`su-chip ${active ? 'su-chip--on' : ''} ${disabled ? 'su-chip--disabled' : ''}`}
            onClick={() => !disabled && onChange(toggle(selected, o))}
            aria-pressed={active}
          >
            {active && <span className="su-chip__check">✓</span>}
            {o}
          </button>
        );
      })}
    </div>
  );
}

function RadioCards({ options, value, onChange }) {
  return (
    <div className="su-radio-cards">
      {options.map(o => (
        <button
          key={o} type="button"
          className={`su-radio-card ${value === o ? 'su-radio-card--on' : ''}`}
          onClick={() => onChange(o)}
          aria-pressed={value === o}
        >
          {value === o && <span className="su-radio-card__dot" />}
          {o}
        </button>
      ))}
    </div>
  );
}

function StarRating({ value, onChange, label }) {
  const [hover, setHover] = useState(0);
  const labels = ['', 'Not important', 'Slightly important', 'Moderately important', 'Very important', 'Essential'];
  return (
    <div className="su-stars-wrap">
      {label && <span className="su-stars-label">{label}</span>}
      <div className="su-stars">
        {[1,2,3,4,5].map(n => (
          <button
            key={n} type="button"
            className={`su-star ${n <= (hover || value) ? 'su-star--on' : ''}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(n)}
            aria-label={`${n} star`}
          >★</button>
        ))}
      </div>
      <span className="su-stars-hint">{labels[hover || value] || 'Tap to rate'}</span>
    </div>
  );
}

function Field({ label, error, children, hint }) {
  return (
    <div className={`su-field ${error ? 'su-field--error' : ''}`}>
      {label && <label className="su-label">{label}</label>}
      {hint && <span className="su-hint">{hint}</span>}
      {children}
      {error && <span className="su-field-msg">{error}</span>}
    </div>
  );
}

function Input({ ...props }) {
  return <input className="su-input" {...props} />;
}

function Select({ options, value, onChange, placeholder }) {
  return (
    <select className="su-select" value={value} onChange={e => onChange(e.target.value)}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// ─── step content components ──────────────────────────────────────────────────
function StepAccount({ data, set, errors }) {
  const [showPw, setShowPw] = useState(false);
  return (
    <div className="su-step-content">
      <p className="su-step-desc">Let's start with your login credentials.</p>
      <Field label="Email address" error={errors.email}>
        <Input
          type="email" autoComplete="email" autoFocus
          value={data.email} onChange={e => set('email', e.target.value)}
          placeholder="you@example.com"
        />
      </Field>
      <Field label="Password" error={errors.password} hint="Minimum 8 characters">
        <div className="su-input-wrap">
          <Input
            type={showPw ? 'text' : 'password'} autoComplete="new-password"
            value={data.password} onChange={e => set('password', e.target.value)}
            placeholder="Create a strong password"
          />
          <button type="button" className="su-pw-toggle" onClick={() => setShowPw(s => !s)} aria-label="toggle password">
            {showPw ? '🙈' : '👁'}
          </button>
        </div>
      </Field>
    </div>
  );
}

function StepPersonal({ data, set, errors }) {
  return (
    <div className="su-step-content">
      <p className="su-step-desc">Tell us a bit about yourself.</p>
      <div className="su-grid-2">
        <Field label="Full name" error={errors.name}>
          <Input value={data.name} onChange={e => set('name', e.target.value)} placeholder="Your name" autoFocus />
        </Field>
        <Field label="Age" error={errors.age}>
          <Input type="number" min="18" max="99" value={data.age} onChange={e => set('age', e.target.value)} placeholder="Your age" />
        </Field>
      </div>
      <div className="su-grid-2">
        <Field label="Occupation" error={errors.occupation}>
          <Input value={data.occupation} onChange={e => set('occupation', e.target.value)} placeholder="What do you do?" />
        </Field>
        <Field label="City / Region">
          <Input value={data.location} onChange={e => set('location', e.target.value)} placeholder="Where are you based?" />
        </Field>
      </div>
      <Field label="Gender" error={errors.gender}>
        <RadioCards options={OPTIONS.gender} value={data.gender} onChange={v => set('gender', v)} />
      </Field>
      <Field label="Your education level">
        <Select options={OPTIONS.educationLevel} value={data.educationLevel} onChange={v => set('educationLevel', v)} placeholder="Select level" />
      </Field>
    </div>
  );
}

function StepValues({ data, set }) {
  return (
    <div className="su-step-content">
      <p className="su-step-desc">What matters most to you in a partner?</p>
      <Field label="Top qualities you need (pick up to 3)" hint="These are your non-negotiables">
        <ChipGroup options={OPTIONS.qualities} selected={data.qualities} onChange={v => set('qualities', v)} max={3} />
      </Field>
      <Field label="Personality traits you admire">
        <ChipGroup options={OPTIONS.traits} selected={data.traits} onChange={v => set('traits', v)} />
      </Field>
      <Field label="Your hobbies & interests">
        <ChipGroup options={OPTIONS.hobbies} selected={data.hobbies} onChange={v => set('hobbies', v)} />
      </Field>
    </div>
  );
}

function StepCulture({ data, set }) {
  return (
    <div className="su-step-content">
      <p className="su-step-desc">Your background shapes who you are.</p>
      <Field label="Cultural or religious background">
        <ChipGroup options={OPTIONS.cultural} selected={data.cultural} onChange={v => set('cultural', v)} />
      </Field>
      <StarRating value={data.importanceCulture} onChange={v => set('importanceCulture', v)} label="How important is a shared background to you?" />
      <Field label="Family traditions that matter to you">
        <ChipGroup options={OPTIONS.traditions} selected={data.traditions} onChange={v => set('traditions', v)} />
      </Field>
      <div className="su-grid-2">
        <Field label="Your educational background">
          <Select options={OPTIONS.educationLevel} value={data.educationBackground} onChange={v => set('educationBackground', v)} placeholder="Select" />
        </Field>
        <div>
          <StarRating value={data.importanceEducation} onChange={v => set('importanceEducation', v)} label="Partner's education matters?" />
        </div>
      </div>
      <Field label="Career & work-life balance">
        <RadioCards options={OPTIONS.careerGoals} value={data.careerGoals} onChange={v => set('careerGoals', v)} />
      </Field>
    </div>
  );
}

function StepLifestyle({ data, set }) {
  return (
    <div className="su-step-content">
      <p className="su-step-desc">What does your ideal life look like?</p>
      <Field label="Long-term goals">
        <ChipGroup options={OPTIONS.longTermGoals} selected={data.longTermGoals} onChange={v => set('longTermGoals', v)} />
      </Field>
      <StarRating value={data.importanceGoals} onChange={v => set('importanceGoals', v)} label="How important are shared life goals?" />
      <Field label="Lifestyle preferences">
        <ChipGroup options={OPTIONS.lifestyle} selected={data.lifestyle} onChange={v => set('lifestyle', v)} />
      </Field>
      <Field label="Health & wellness">
        <ChipGroup options={OPTIONS.health} selected={data.health} onChange={v => set('health', v)} />
      </Field>
      <StarRating value={data.importanceHealth} onChange={v => set('importanceHealth', v)} label="How important is health in a relationship?" />
      <Field label="Medical conditions to share (optional)" hint="Only share what you're comfortable with">
        <textarea className="su-textarea" rows={3} value={data.medicalConditions} onChange={e => set('medicalConditions', e.target.value)} placeholder="Any relevant health considerations..." />
      </Field>
    </div>
  );
}

function StepFamily({ data, set }) {
  return (
    <div className="su-step-content">
      <p className="su-step-desc">Family plays a big role in South Asian life.</p>
      <Field label="Your expectations for family involvement">
        <ChipGroup options={OPTIONS.familyExpect} selected={data.familyExpect} onChange={v => set('familyExpect', v)} />
      </Field>
      <StarRating value={data.roleOfFamily} onChange={v => set('roleOfFamily', v)} label="How much should family influence decisions?" />
      <Field label="What you expect from your partner regarding your family">
        <ChipGroup options={OPTIONS.partnerFamily} selected={data.partnerFamily} onChange={v => set('partnerFamily', v)} />
      </Field>
      <Field label="How you handle conflict">
        <ChipGroup options={OPTIONS.conflictHandling} selected={data.conflictHandling} onChange={v => set('conflictHandling', v)} />
      </Field>
      <Field label="Preferred conflict resolution strategies">
        <ChipGroup options={OPTIONS.conflictStrategies} selected={data.conflictStrategies} onChange={v => set('conflictStrategies', v)} />
      </Field>
    </div>
  );
}

function StepMarriage({ data, set }) {
  return (
    <div className="su-step-content">
      <p className="su-step-desc">Almost there — the big picture questions.</p>
      <Field label="Roles & responsibilities in marriage">
        <ChipGroup options={OPTIONS.marriageRoles} selected={data.marriageRoles} onChange={v => set('marriageRoles', v)} />
      </Field>
      <Field label="Stance on children">
        <RadioCards options={OPTIONS.children} value={data.children} onChange={v => set('children', v)} />
      </Field>
      <Field label="Financial approach">
        <RadioCards options={OPTIONS.finances} value={data.finances} onChange={v => set('finances', v)} />
      </Field>
      <Field label="Household duties">
        <RadioCards options={OPTIONS.householdDuties} value={data.householdDuties} onChange={v => set('householdDuties', v)} />
      </Field>
      <Field label="Vision for your future together">
        <ChipGroup options={OPTIONS.futureVision} selected={data.futureVision} onChange={v => set('futureVision', v)} />
      </Field>
      <Field label="Anything else you'd like to share?" hint="Optional — use this to express anything the form didn't cover">
        <textarea className="su-textarea" rows={4} value={data.additionalInfo} onChange={e => set('additionalInfo', e.target.value)} placeholder="Tell your future partner something unique about you..." />
      </Field>
    </div>
  );
}

function StepFinish({ submitting, error }) {
  return (
    <div className="su-step-content su-step-finish">
      {submitting ? (
        <>
          <div className="su-finish-spinner" />
          <p className="su-finish-title">Creating your profile…</p>
        </>
      ) : error ? (
        <>
          <div className="su-finish-icon su-finish-icon--error">✕</div>
          <p className="su-finish-title">Something went wrong</p>
          <p className="su-finish-sub">{error}</p>
        </>
      ) : (
        <>
          <div className="su-finish-icon">✓</div>
          <p className="su-finish-title">Profile created!</p>
          <p className="su-finish-sub">Taking you to your account…</p>
        </>
      )}
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
export default function SignUpForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState({});

  const [data, setData] = useState({
    // account
    email: '', password: '',
    // personal
    name: '', age: '', gender: '', educationLevel: '', occupation: '', location: '',
    // values
    qualities: [], traits: [], hobbies: [],
    // culture
    cultural: [], importanceCulture: 0, traditions: [],
    educationBackground: '', importanceEducation: 0, careerGoals: '',
    // lifestyle
    longTermGoals: [], importanceGoals: 0, lifestyle: [],
    health: [], importanceHealth: 0, medicalConditions: '',
    // family
    familyExpect: [], roleOfFamily: 0, partnerFamily: [],
    conflictHandling: [], conflictStrategies: [],
    // marriage
    marriageRoles: [], children: '', finances: '', householdDuties: '',
    futureVision: [], additionalInfo: '',
  });

  const set = (key, val) => setData(d => ({ ...d, [key]: val }));

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!validateEmail(data.email)) e.email = 'Enter a valid email address';
      if (data.password.length < 8) e.password = 'Password must be at least 8 characters';
    }
    if (step === 1) {
      if (!data.name.trim()) e.name = 'Name is required';
      if (!data.age || data.age < 18) e.age = 'Must be 18 or older';
      if (!data.gender) e.gender = 'Please select a gender';
      if (!data.occupation.trim()) e.occupation = 'Occupation is required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validate()) return;
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => {
    setErrors({});
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setStep(STEPS.length - 1);
    setSubmitting(true);
    setSubmitError('');
    try {
      const cred = await createUserWithEmailAndPassword(auth, data.email, data.password);
      const uid = cred.user.uid;
      const toArr = v => Array.isArray(v) ? v : (v ? [v] : []);
      await setDoc(doc(db, 'users', uid), {
        email: data.email, name: data.name, gender: data.gender,
        age: Number(data.age) || 0, occupation: data.occupation, location: data.location,
        educationLevel: data.educationLevel, educationBackground: data.educationBackground,
        careerGoals: data.careerGoals, medicalConditions: data.medicalConditions,
        children: data.children || 'Undecided', finances: data.finances, householdDuties: data.householdDuties,
        additionalInformation: data.additionalInfo,
        importanceOfCulturalBackground: data.importanceCulture,
        importanceOfPartnerEducation: data.importanceEducation,
        importanceOfLifeGoals: data.importanceGoals,
        importanceOfHealth: data.importanceHealth,
        roleOfFamily: data.roleOfFamily,
        hobbies: toArr(data.hobbies), culturalBackground: toArr(data.cultural),
        personalityTraits: toArr(data.traits), nonNegotiableQualities: toArr(data.qualities),
        longTermGoals: toArr(data.longTermGoals), lifestyleChoices: toArr(data.lifestyle),
        familyExpectations: toArr(data.familyExpect), familyTraditions: toArr(data.traditions),
        healthConsiderations: toArr(data.health), conflictHandling: toArr(data.conflictHandling),
        conflictStrategies: toArr(data.conflictStrategies), futureVision: toArr(data.futureVision),
        marriageRoles: toArr(data.marriageRoles), partnerFamilyExpectations: toArr(data.partnerFamily),
        createdAt: new Date(),
      });
      setTimeout(() => navigate('/my-account'), 1200);
    } catch (err) {
      const map = {
        'auth/email-already-in-use': 'An account with this email already exists.',
        'auth/weak-password': 'Please choose a stronger password.',
        'auth/invalid-email': 'That email address looks invalid.',
      };
      setSubmitError(map[err.code] || err.message);
      setSubmitting(false);
    }
  };

  const isLast = step === STEPS.length - 2;
  const isFinish = step === STEPS.length - 1;
  const progress = Math.round((step / (STEPS.length - 1)) * 100);

  const stepContent = [
    <StepAccount data={data} set={set} errors={errors} />,
    <StepPersonal data={data} set={set} errors={errors} />,
    <StepValues data={data} set={set} />,
    <StepCulture data={data} set={set} />,
    <StepLifestyle data={data} set={set} />,
    <StepFamily data={data} set={set} />,
    <StepMarriage data={data} set={set} />,
    <StepFinish submitting={submitting} error={submitError} />,
  ];

  return (
    <div className="su-root">
      {/* header */}
      <header className="su-header">
        <button
          className="su-back-btn"
          onClick={step === 0 ? () => navigate(-1) : back}
          disabled={isFinish}
          aria-label="Go back"
        >
          ← {step === 0 ? 'Back' : STEPS[step - 1].label}
        </button>
        <div className="su-logo">⬡ Look4Partner</div>
        <span className="su-step-count">{isFinish ? '' : `${step + 1} / ${STEPS.length - 1}`}</span>
      </header>

      {/* progress bar */}
      {!isFinish && (
        <div className="su-progress-bar" role="progressbar" aria-valuenow={progress}>
          <div className="su-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      )}

      {/* step tabs */}
      {!isFinish && (
        <nav className="su-tabs" aria-label="Form steps">
          {STEPS.slice(0, -1).map((s, i) => (
            <div
              key={s.id}
              className={`su-tab ${i === step ? 'su-tab--active' : ''} ${i < step ? 'su-tab--done' : ''}`}
              aria-current={i === step ? 'step' : undefined}
            >
              <span className="su-tab__icon">{i < step ? '✓' : s.icon}</span>
              <span className="su-tab__label">{s.label}</span>
            </div>
          ))}
        </nav>
      )}

      {/* main card */}
      <main className="su-main">
        <div className="su-card" key={step}>
          {!isFinish && (
            <h2 className="su-step-title">
              <span className="su-step-icon">{STEPS[step].icon}</span>
              {STEPS[step].label}
            </h2>
          )}
          {stepContent[step]}
        </div>

        {/* nav buttons */}
        {!isFinish && (
          <div className="su-nav">
            {isLast ? (
              <button className="su-btn-primary su-btn-submit" onClick={handleSubmit}>
                Create my profile 🎉
              </button>
            ) : (
              <button className="su-btn-primary" onClick={next}>
                Continue →
              </button>
            )}
            <p className="su-nav-hint">
              {step === 0
                ? 'Already have an account? '
                : `Step ${step + 1} of ${STEPS.length - 1} · `}
              {step === 0
                ? <button className="su-link" type="button" onClick={() => navigate('/signin')}>Sign in</button>
                : <span>Your progress is saved as you go</span>}
            </p>
          </div>
        )}

        {submitError && isFinish && !submitting && (
          <div className="su-nav">
            <button className="su-btn-primary" onClick={() => { setStep(STEPS.length - 2); setSubmitError(''); }}>
              ← Go back and try again
            </button>
          </div>
        )}
      </main>
    </div>
  );
}